import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, GiftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePermissionStore } from '../stores/permissionStore';
import { authService } from '../services/auth';
import Turnstile from '../components/Turnstile';

const { Title } = Typography;

const CUSTOMER_SERVICE_URL = 'https://t.me/webpage1688';
const CUSTOMER_SERVICE_HANDLE = '@webpage1688';

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;

interface RegisterForm {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
  invite_code?: string;
  telegram?: string;
  wechat?: string;
}

const RegisterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showCustomerService, setShowCustomerService] = useState(false);
  const [turnstileVerified, setTurnstileVerified] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      setShowCustomerService(
        hostname === 'console.ipcloak.ai' || hostname.endsWith('.ipcloak.ai')
      );
    }

    const inviteCode = searchParams.get('invite');
    if (inviteCode) {
      form.setFieldsValue({ invite_code: inviteCode });
    }
  }, [form, searchParams, isAuthenticated, navigate]);

  const onFinish = async (values: RegisterForm) => {
    setLoading(true);
    try {
      const response = await authService.register({
        username: values.username.trim(),
        email: values.email.trim(),
        password: values.password,
        invite_code: values.invite_code?.trim() || undefined,
        telegram: values.telegram?.trim() || undefined,
        wechat: values.wechat?.trim() || undefined,
      });

      const { user, token, permissions } = response;

      login(user, token);

      if (permissions && permissions.length > 0) {
        usePermissionStore.getState().setPermissions(permissions);
      }

      message.success('注册成功');
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message || '注册失败';
      if (error?.response?.status === 409) {
        message.error('用户名或邮箱已存在');
      } else {
        message.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      {showCustomerService && (
        <>
          <style>
            {`
              @keyframes registerPageMarquee {
                0% { transform: translateX(100%); }
                100% { transform: translateX(-100%); }
              }
            `}
          </style>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            background: 'rgba(0, 0, 0, 0.65)',
            color: '#fff',
            fontSize: 12,
            padding: '4px 0',
            zIndex: 1000,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}>
            <span style={{
              display: 'inline-block',
              paddingLeft: '100%',
              animation: 'registerPageMarquee 15s linear infinite',
            }}>
              请合法使用本平台，严禁将其用于任何非法业务
            </span>
          </div>
        </>
      )}
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            访客管理平台
          </Title>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            创建您的账户
          </p>
        </div>

        <Form
          name="register"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少 3 个字符' },
              { max: 32, message: '用户名最多 32 个字符' },
              {
                pattern: USERNAME_REGEX,
                message: '用户名仅允许字母、数字和下划线',
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入正确的邮箱格式' },
              { max: 128, message: '邮箱最多 128 个字符' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="邮箱"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少 6 位' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            dependencies={['password']}
            rules={[
              { required: true, message: '请再次输入密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="确认密码"
            />
          </Form.Item>

          <Form.Item name="invite_code">
            <Input
              prefix={<GiftOutlined />}
              placeholder="邀请码（选填）"
            />
          </Form.Item>

          <Form.Item
            name="telegram"
            extra={<span style={{ color: '#1890ff', fontSize: 12 }}>建议填写，便于客服联系对接</span>}
          >
            <Input
              prefix={<span style={{ fontSize: 12, marginRight: 2 }}>✈</span>}
              placeholder="Telegram 账号（选填）"
              maxLength={100}
            />
          </Form.Item>

          <Form.Item name="wechat">
            <Input
              prefix={<span style={{ fontSize: 12, marginRight: 2 }}>💬</span>}
              placeholder="微信号（选填）"
              maxLength={100}
            />
          </Form.Item>

          <Turnstile
            onVerify={() => setTurnstileVerified(true)}
            onExpire={() => setTurnstileVerified(false)}
            onError={() => setTurnstileVerified(false)}
            onUnsupported={() => setTurnstileVerified(true)}
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!turnstileVerified}
              style={{ width: '100%' }}
            >
              {turnstileVerified ? '注册' : '人机验证中...'}
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Typography.Link onClick={() => navigate('/login')}>
              已有账号？返回登录
            </Typography.Link>
          </div>

          {showCustomerService && (
            <div style={{ textAlign: 'center', marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Typography.Link
                href={CUSTOMER_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                联系客服 Telegram：{CUSTOMER_SERVICE_HANDLE}
              </Typography.Link>
              <Typography.Link
                href="https://t.me/ipcloak"
                target="_blank"
                rel="noopener noreferrer"
              >
                加入交流群：@ipcloak
              </Typography.Link>
            </div>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default RegisterPage;
